import { parseNumber } from "@/tools/converters/numberInput";

/** parseNumber that also accepts a leading/trailing currency symbol or % sign ("$29.99", "10%"). */
const pn = (s: string): number | null => parseNumber(String(s ?? "").replace(/[$€£¥₹%]/g, ""));

/** `credit` marks a deliberate credit/refund line: it is always subtracted, however the price was typed. */
export interface InvoiceLine { id?: string; description: string; quantity: string; unitPrice: string; credit?: boolean }

export interface InvoiceTotals {
  lines: { description: string; qty: number; price: number; amount: number; credit: boolean }[];
  subtotal: number; discount: number; taxable: number; tax: number; shipping: number; total: number; paid: number; balanceDue: number;
}

const num = (s: string) => {
  const p = pn(String(s ?? ""));
  if (p !== null) return p;
  const n = Number(String(s).replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
};
export const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

/**
 * Lines with a description are billed; discount is a percentage of the subtotal; tax applies after the discount;
 * shipping is added after tax (untaxed); an amount paid / deposit gives the balance due.
 */
export function invoiceTotals(items: InvoiceLine[], discountPct: string, taxPct: string, shippingStr = "", paidStr = ""): InvoiceTotals {
  const lines = items.filter((i) => i.description.trim()).map((i) => {
    const qty = num(i.quantity), price = num(i.unitPrice);
    const credit = !!i.credit;
    const amount = round2(credit ? -Math.abs(qty * price) : qty * price);
    return { description: i.description.trim(), qty, price: credit ? -Math.abs(price) : price, amount, credit };
  });
  const subtotal = round2(lines.reduce((s, l) => s + l.amount, 0));
  const d = Math.min(100, Math.max(0, num(discountPct)));
  const discount = round2((subtotal * d) / 100);
  const taxable = round2(subtotal - discount);
  const tax = round2((taxable * Math.max(0, num(taxPct))) / 100);
  const shipping = round2(Math.max(0, num(shippingStr)));
  const total = round2(taxable + tax + shipping);
  const paid = round2(Math.max(0, num(paidStr)));
  return { lines, subtotal, discount, taxable, tax, shipping, total, paid, balanceDue: round2(total - paid) };
}

export interface InvoiceError { field: string; message: string }

const bad = (s: string) => s.trim() !== "" && pn(s) === null;

export interface InvoiceForm {
  businessName: string; clientName: string; invoiceDate: string; dueDate: string;
  items: InvoiceLine[]; discountPct: string; taxPct: string; shipping: string; paid: string;
}

/**
 * Every problem with the form, in on-screen order. `field` is the id of the input to mark/focus:
 * inv-business, inv-client, inv-date, inv-due, inv-item-<id>-desc|qty|price, inv-discount, inv-tax, inv-shipping, inv-paid.
 * Checked BEFORE a trial use is charged.
 */
export function invoiceErrors(f: InvoiceForm): InvoiceError[] {
  const out: InvoiceError[] = [];
  if (!f.businessName.trim()) out.push({ field: "inv-business", message: "Enter your business name." });
  if (!f.clientName.trim()) out.push({ field: "inv-client", message: "Enter the client name." });
  if (!f.invoiceDate) out.push({ field: "inv-date", message: "Choose the invoice date." });
  if (f.dueDate && f.invoiceDate && f.dueDate < f.invoiceDate) out.push({ field: "inv-due", message: "The due date can't be before the invoice date." });
  const used = f.items.filter((i) => i.description.trim());
  if (used.length === 0) {
    const first = f.items[0];
    out.push({ field: `inv-item-${first?.id ?? "1"}-desc`, message: "Add at least one line item with a description." });
  }
  for (const i of f.items) {
    const key = i.id ?? "1";
    const touched = i.description.trim() || i.unitPrice.trim();
    if (!touched) continue;
    if (!i.description.trim()) out.push({ field: `inv-item-${key}-desc`, message: "Describe this line item." });
    const q = pn(i.quantity);
    if (q === null) out.push({ field: `inv-item-${key}-qty`, message: "Enter a quantity above 0." });
    else if (!(q > 0)) out.push({ field: `inv-item-${key}-qty`, message: "Quantity must be above 0. For a refund or credit, tick Credit instead." });
    const p = pn(i.unitPrice);
    if (p === null) out.push({ field: `inv-item-${key}-price`, message: i.unitPrice.trim() === "" ? "Enter a price." : "Price must be a number." });
    else if (p < 0 && !i.credit) out.push({ field: `inv-item-${key}-price`, message: "A negative price is only allowed on a credit line. Tick Credit." });
  }
  const pct = (v: string, field: string, name: string) => {
    if (bad(v)) out.push({ field, message: `${name} must be a number.` });
    else if (v.trim() !== "") { const n = pn(v)!; if (n < 0 || n > 100) out.push({ field, message: `${name} must be between 0 and 100.` }); }
  };
  pct(f.discountPct, "inv-discount", "Discount");
  pct(f.taxPct, "inv-tax", "Tax");
  for (const [v, field, name] of [[f.shipping, "inv-shipping", "Shipping"], [f.paid, "inv-paid", "Amount paid"]] as const) {
    if (bad(v)) out.push({ field, message: `${name} must be a number.` });
    else if (v.trim() !== "" && pn(v)! < 0) out.push({ field, message: `${name} can't be negative.` });
  }
  return out;
}

/** Kept for callers that want a single sentence: the first problem, or null. */
export function invoiceProblem(businessName: string, clientName: string, items: InvoiceLine[]): string | null {
  if (!businessName.trim() || !clientName.trim()) return "Please fill in your business name and the client name.";
  const used = items.filter((i) => i.description.trim());
  if (used.length === 0) return "Add at least one line item with a description.";
  if (used.some((i) => !(num(i.quantity) > 0))) return "Every line item needs a quantity above 0.";
  if (used.some((i) => i.unitPrice.trim() === "" || (num(i.unitPrice) < 0 && !i.credit))) return "Every line item needs a price (0 or more).";
  return null;
}

export type PaymentTerms = "custom" | "receipt" | "net15" | "net30" | "net60";
export const TERMS_DAYS: Record<Exclude<PaymentTerms, "custom">, number> = { receipt: 0, net15: 15, net30: 30, net60: 60 };

/** ISO date plus whole days (UTC arithmetic, so daylight-saving changes can't shift the day). Empty for a bad date. */
/** Text printed next to the due date for the chosen payment terms; empty for a custom date. */
export const TERMS_LABEL: Record<Exclude<PaymentTerms, "custom">, string> = { receipt: "Due on receipt", net15: "Net 15", net30: "Net 30", net60: "Net 60" };
export function termsLine(terms: PaymentTerms): string {
  return terms === "custom" ? "" : `Payment terms: ${TERMS_LABEL[terms]}`;
}

export function addDaysIso(iso: string, days: number): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return "";
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3] + days));
  return d.toISOString().slice(0, 10);
}

/** "INV-009" -> "INV-010" (keeps zero padding); text with no number gets "-2"; empty gives INV-001. */
export function nextInvoiceNumber(last: string | null | undefined): string {
  const t = (last ?? "").trim();
  if (!t) return "INV-001";
  const m = /^(.*?)(\d+)(\D*)$/.exec(t);
  if (!m) return `${t}-2`;
  const next = String(BigInt(m[2]) + BigInt(1)).padStart(m[2].length, "0");
  return `${m[1]}${next}${m[3]}`;
}
